/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { actions } from "../../actions";
import { services } from "../../services";
import {
  Record,
  Web3Data,
  ResellPoolContract,
  RentalPoolContract,
} from "../../types/nft/approve.asset";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { ChevronDownIcon } from "lucide-react";
import { useToast } from "../../components/ui/use-toast";
import { backendServices } from "../../services/backend.service";

const ApproveButton: React.FC<{ record: Record }> = ({ record }) => {
  const { toast } = useToast();
  const [isPropertySet, setIsPropertySet] = useState<boolean>(false);
  const [isRentalSet, setIsRentalSet] = useState<boolean>(false);
  const [resellInstance, setResellInstance] =
    useState<ResellPoolContract | null>(null);
  const [rentalInstance, setRentalInstance] =
    useState<RentalPoolContract | null>(null);
  const [selectedAction, setSelectedAction] = useState<string | false>(false);
  const [approved, setApproved] = useState<boolean>(false);
  const [web3Data, setWeb3Data] = useState<Web3Data | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    handleApprove();
  }, [web3Data, record.assetAddress]);

  const handleApprove = async () => {
    try {
      setLoading(true);
      if (!web3Data) {
        const data = await services.getWeb3();
        if (data) {
          setWeb3Data(data);
        } else {
          throw new Error("Failed to get Web3 data");
        }
      } else {
        const resellPoolContractInstance =
          await actions.getResellPoolContract();
        const rentalPoolContractInstance =
          await actions.getRentalPoolContract();

        if (!resellPoolContractInstance || !rentalPoolContractInstance) {
          throw new Error("Failed to get contract instances");
        }

        setResellInstance(resellPoolContractInstance as ResellPoolContract);
        setRentalInstance(rentalPoolContractInstance as RentalPoolContract);

        const isPropertySet = await resellPoolContractInstance.methods
          .isPropertyAsset(record.assetAddress)
          .call();
        const isRentalSet = await rentalPoolContractInstance.methods
          .isPropertyListed(record.assetAddress)
          .call();

        setIsPropertySet(Boolean(isPropertySet));
        setIsRentalSet(Boolean(isRentalSet));

        if (isPropertySet) {
          setSelectedAction("Resell");
        } else if (isRentalSet) {
          setSelectedAction("Rental");
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySet = async () => {
    if (!resellInstance || !web3Data) return;
    try {
      setLoading(true);
      await resellInstance.methods.setPropertyAsset(record.assetAddress).send({
        from: web3Data.accounts[0],
      });

      // Call API to update asset resell status in database
      try {
        await backendServices.updateAssetResellStatus(record._id);
      } catch (apiError) {
        toast({
          description:
            "Property set on resell, but failed to update database status",
          variant: "destructive",
        });
      }

      setApproved(true);
      toast({ description: "Property set on resell", variant: "default" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast({ description: `${error}`, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleRentalSet = async () => {
    if (!rentalInstance || !web3Data) return;
    try {
      setLoading(true);
      await rentalInstance.methods
        .setPropertyAddress(record.assetAddress)
        .send({
          from: web3Data.accounts[0],
        });
      setApproved(true);
      toast({ description: "Property set on rental", variant: "default" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast({ description: `${error}`, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (loading) return "Approving...";
    if (isPropertySet) return "On Resell";
    if (isRentalSet) return "On Rental";
    return "Set Now";
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-24 flex items-center justify-between border-surface-2 text-surface-2"
          disabled={loading}
          style={{ color: "#095D52" }}
        >
          {getButtonText()}
          <ChevronDownIcon className="h-4 w-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        <DropdownMenuItem
          onClick={handlePropertySet}
          disabled={isPropertySet}
          className={
            isPropertySet ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }
        >
          {isPropertySet ? "On Resell" : "Set as Resell"}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleRentalSet}
          disabled={isRentalSet}
          className={
            isRentalSet ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }
        >
          {isRentalSet ? "On Rental" : "Set as Rental"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ApproveButton;
